import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Search, RefreshCw, History, User,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, ArrowUpDown, Loader2,
    Columns3, Eye, EyeOff, Check
} from "lucide-react";
import baseapi from "../services/baseapi";
import { showError } from "../utils/toast";
import { ItemsPerPageDropdown } from "../components/ItemsPerPageDropdown ";
import { useClientLevel } from "../hooks/useClientLevel";

interface AuditLog {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    user: string;
    action: string;
    organization_id: number;
    client_id: number;
    details: string;
    status: string;
    record_id: string;
    previous_values: string;
    current_values: string;
}

export type AuditLogColumnKey = 'user' | 'action' | 'description' | 'status' | 'created';

export const AuditLogs = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(true);

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Column visibility state
    const [showColumnFilter, setShowColumnFilter] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<Record<AuditLogColumnKey, boolean>>({
        user: true,
        action: true,
        description: true,
        status: true,
        created: true,
    });

    const columnOptions: { key: AuditLogColumnKey; label: string }[] = useMemo(() => [
        { key: 'user', label: 'User' },
        { key: 'action', label: 'Action' },
        { key: 'description', label: 'Description' },
        { key: 'status', label: 'Status' },
        { key: 'created', label: 'Timestamp' },
    ], []);

    const handleColumnToggle = (column: AuditLogColumnKey) => {
        setVisibleColumns((prev) => ({
            ...prev,
            [column]: !prev[column],
        }));
    };

    const toggleAllColumns = () => {
        const allVisible = Object.values(visibleColumns).every(Boolean);
        setVisibleColumns({
            user: !allVisible,
            action: !allVisible,
            description: !allVisible,
            status: !allVisible,
            created: !allVisible,
        });
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (showColumnFilter && !target.closest('[data-col-menu]')) {
                setShowColumnFilter(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showColumnFilter]);

    // Pagination state
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Sync with URL params
    const [currentPage, setCurrentPage] = useState<number>(() => {
        const p = searchParams.get('page');
        return p ? parseInt(p, 10) : 1;
    });

    const [itemsPerPage, setItemsPerPage] = useState<number>(() => {
        const l = searchParams.get('limit');
        return l ? parseInt(l, 10) : 30;
    });

    const [sortBy, setSortBy] = useState<string>(searchParams.get('sort_by') || 'created');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>((searchParams.get('sort_order') as 'asc' | 'desc') || 'desc');

    const level = useClientLevel();

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // Sync state from URL params
    useEffect(() => {
        const p = searchParams.get('page');
        const l = searchParams.get('limit');
        const sb = searchParams.get('sort_by');
        const so = searchParams.get('sort_order');

        if (p) {
            const pageNum = parseInt(p, 10);
            if (!isNaN(pageNum) && pageNum !== currentPage) setCurrentPage(pageNum);
        }
        if (l) {
            const limitNum = parseInt(l, 10);
            if (!isNaN(limitNum) && limitNum !== itemsPerPage) setItemsPerPage(limitNum);
        }
        if (sb && sb !== sortBy) setSortBy(sb);
        if (so && (so === 'asc' || so === 'desc') && so !== sortOrder) setSortOrder(so as 'asc' | 'desc');
    }, [searchParams, currentPage, itemsPerPage, sortBy, sortOrder]);

    // Debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setCurrentPage(1);
                setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('page', '1');
                    return next;
                }, { replace: true });
            }
        }, 500);

        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch, setSearchParams]);

    const fetchLogs = useCallback(async (skip = 0, limit = itemsPerPage) => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (level?.id) {
                queryObj["client_id"] = level.id;
            }
            if (debouncedSearch) {
                queryObj.$or = [
                    { user: { $ilike: `%${debouncedSearch}%` } },
                    { details: { $ilike: `%${debouncedSearch}%` } },
                    { action: { $ilike: `%${debouncedSearch}%` } },
                    { status: { $ilike: `%${debouncedSearch}%` } },
                ];
            }

            // Build API payload
            const payload: any = {
                skip,
                limit,
                sort_by: sortBy,
                sort_order: sortOrder,
                query: JSON.stringify(queryObj)
            };

            const response = await baseapi.get("/audit_logs", { params: payload });

            if (response.data.status) {
                setLogs(response.data.data);
                setTotalPages(Math.ceil((response.data.total || response.data.data.length) / limit));
                setTotalItems(response.data.total || response.data.data.length);
            } else {
                setLogs([]);
                setTotalPages(0);
                setTotalItems(0);
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    }, [itemsPerPage, debouncedSearch, level, sortBy, sortOrder]);

    useEffect(() => {
        fetchLogs((currentPage - 1) * itemsPerPage, itemsPerPage);
    }, [currentPage, itemsPerPage, fetchLogs]);

    const handleRefresh = () => {
        fetchLogs((currentPage - 1) * itemsPerPage, itemsPerPage);
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('page', String(page));
                return next;
            }, { replace: true });
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(1);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('limit', String(value));
            next.set('page', '1');
            return next;
        }, { replace: true });
    };

    const handleSort = (field: string) => {
        let nextOrder: 'asc' | 'desc' = 'asc';
        if (sortBy === field) {
            nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        }
        setSortBy(field);
        setSortOrder(nextOrder);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('sort_by', field);
            next.set('sort_order', nextOrder);
            return next;
        }, { replace: true });
    };

    const SortIcon = ({ field }: { field: string }) => {
        if (sortBy !== field) return <ArrowUpDown className="w-2.5 h-2.5 ml-1 text-gray-400 dark:text-gray-500 transition-opacity" />;
        return sortOrder === 'asc'
            ? <ArrowUp className="w-2.5 h-2.5 ml-1 text-orange-500" />
            : <ArrowDown className="w-2.5 h-2.5 ml-1 text-orange-500" />;
    };

    const parseDescription = (log: AuditLog) => {
        try {
            const parsed =
                typeof log.details === "string"
                    ? JSON.parse(log.details)
                    : log.details;

            if (log.action === "camera_asset_mac_conflict_merge") {
                return `
                    <div class="flex flex-col gap-0.5">
                        <span class="text-orange-600 dark:text-orange-400 font-bold">${parsed.message || 'Camera MAC Conflict Merge'}</span>
                        <div class="flex items-center gap-3 opacity-80 text-[9px] uppercase tracking-wider text-gray-600 dark:text-gray-300">
                            <span>MAC: <span class="text-blue-600 dark:text-blue-400 font-bold">${parsed.mac_address || "-"}</span></span>
                            <span>KEPT ID: <span class="text-green-600 dark:text-green-400 font-bold">${parsed.kept_asset_id || '-'}</span></span>
                            <span>REMOVED ID: <span class="text-red-600 dark:text-red-400 font-bold">${parsed.removed_asset_id || '-'}</span></span>
                        </div>
                    </div>
                `;
            }

            return parsed.description || parsed.message || "No description provided";
        } catch {
            return log.details || "-";
        }
    };

    const renderActionBadge = (action: string) => {
        const normalized = action.toLowerCase();
        let colorClass = "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-500/15 dark:text-gray-400 dark:border-gray-500/30";

        if (normalized.includes("update") || normalized.includes("edit")) {
            colorClass = "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-400 dark:border-blue-500/30";
        } else if (normalized.includes("create") || normalized.includes("add")) {
            colorClass = "bg-green-50 text-green-700 border-green-200 dark:bg-green-500/15 dark:text-green-400 dark:border-green-500/30";
        } else if (normalized.includes("delete") || normalized.includes("remove")) {
            colorClass = "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/30";
        } else if (normalized.includes("merge")) {
            colorClass = "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/15 dark:text-orange-400 dark:border-orange-500/30";
        }

        return (
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${colorClass}`}>
                {action.replace(/_/g, " ")}
            </span>
        );
    };

    const visibleColsCount = Object.values(visibleColumns).filter(Boolean).length || 1;

    return (
        <div className="bg-gray-50 dark:bg-gray-900 p-2 min-h-screen pb-20 transition-colors duration-200 max-w-full overflow-x-hidden">
            <div className="w-full max-w-full min-w-0 mx-auto">
                {/* Top Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-2.5">
                    {/* Title & Mobile Refresh */}
                    <div className="flex items-center justify-between w-full sm:w-auto">
                        <div className="flex items-center gap-1.5">
                            <div className="bg-orange-600 p-1.5 rounded shadow-lg">
                                <History className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h1 className="font-bold text-lg text-gray-900 dark:text-gray-200 leading-none">Audit Logs</h1>
                            </div>
                            {!loading && (
                                <div className="font-bold text-lg text-gray-700 dark:text-gray-100">
                                    ({totalItems})
                                </div>
                            )}
                        </div>
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="sm:hidden rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shadow-sm shrink-0"
                            title="Refresh logs"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>

                    <div className="flex items-center gap-1.5 sm:gap-2 w-full sm:w-auto">
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="hidden sm:flex rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shadow-sm shrink-0"
                            title="Refresh logs"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <div className="relative flex-1 sm:w-56 sm:flex-initial min-w-0">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search logs..."
                                className="w-full bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-orange-500 outline-none transition-all shadow-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Column Filter Dropdown */}
                        <div className="relative shrink-0" data-col-menu>
                            <button
                                onClick={() => setShowColumnFilter(!showColumnFilter)}
                                className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-all flex items-center gap-1.5 sm:gap-2 shadow-sm"
                            >
                                <Columns3 className="w-3.5 h-3.5" />
                                <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
                            </button>
                            {showColumnFilter && (
                                <div className="absolute top-full mt-2 right-0 w-52 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[110] p-1.5 max-h-[450px] overflow-y-auto">
                                    <div className="px-3 py-1.5 text-[9px] font-bold text-gray-900 dark:text-gray-100 uppercase border-b border-gray-200 dark:border-gray-800 mb-1 flex items-center justify-between">
                                        <span>Toggle Columns</span>
                                        <button
                                            onClick={toggleAllColumns}
                                            className="text-[9px] text-orange-600 hover:text-orange-500 dark:text-orange-400 dark:hover:text-orange-300 transition-colors"
                                        >
                                            {columnOptions.every((o) => visibleColumns[o.key])
                                                ? 'Hide All'
                                                : 'Show All'}
                                        </button>
                                    </div>
                                    {columnOptions.map((option) => (
                                        <button
                                            key={option.key}
                                            onClick={() => handleColumnToggle(option.key)}
                                            className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                                        >
                                            <div className="flex items-center gap-2.5">
                                                {visibleColumns[option.key] ? (
                                                    <Eye className="w-3 h-3 text-orange-500" />
                                                ) : (
                                                    <EyeOff className="w-3 h-3 text-gray-400 dark:text-gray-600" />
                                                )}
                                                {option.label}
                                            </div>
                                            <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${visibleColumns[option.key] ? 'bg-orange-600 border-orange-600' : 'border-gray-300 dark:border-gray-700'}`}>
                                                {visibleColumns[option.key] && <Check className="w-2.5 h-2.5 text-white" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main Table Container */}
                <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl mt-4 transition-colors duration-200 max-w-full">
                    <div className="overflow-x-auto max-w-full">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-gray-200 dark:border-gray-700/50">
                                    {visibleColumns.user && (
                                        <th
                                            className="px-3 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                                            onClick={() => handleSort('user')}
                                        >
                                            <div className="flex items-center gap-1">
                                                <span>User</span>
                                                <SortIcon field="user" />
                                            </div>
                                        </th>
                                    )}

                                    {visibleColumns.action && (
                                        <th
                                            className="px-3 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                                            onClick={() => handleSort('action')}
                                        >
                                            <div className="flex items-center gap-1">
                                                <span>Action</span>
                                                <SortIcon field="action" />
                                            </div>
                                        </th>
                                    )}
                                    {visibleColumns.description && (
                                        <th className="px-3 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest">
                                            <span>Description</span>
                                        </th>
                                    )}
                                    {visibleColumns.status && (
                                        <th
                                            className="px-3 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                                            onClick={() => handleSort('status')}
                                        >
                                            <div className="flex items-center gap-1">
                                                <span>Status</span>
                                                <SortIcon field="status" />
                                            </div>
                                        </th>
                                    )}
                                    {visibleColumns.created && (
                                        <th
                                            className="px-3 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                                            onClick={() => handleSort('created')}
                                        >
                                            <div className="flex items-center gap-1">
                                                <span>Timestamp</span>
                                                <SortIcon field="created" />
                                            </div>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30">
                                {loading ? (
                                    <tr>
                                        <td colSpan={visibleColsCount} className="py-16 text-center text-gray-600 dark:text-gray-100 font-bold uppercase tracking-widest italic">
                                            <div className="flex items-center justify-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin text-orange-500" /> Loading logs
                                            </div>
                                        </td>
                                    </tr>
                                ) : logs.length === 0 ? (
                                    <tr>
                                        <td colSpan={visibleColsCount} className="py-16 text-center text-gray-500 dark:text-gray-100 font-bold uppercase tracking-widest italic">
                                            <div className="flex flex-col items-center gap-2">
                                                <History className="w-6 h-6 text-gray-400 dark:text-gray-600" /> No logs found.
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((log) => (
                                        <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-100 dark:border-gray-700/30">
                                            {visibleColumns.user && (
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <User size={14} className="text-cyan-600 dark:text-cyan-400" />
                                                        <span className="text-gray-900 dark:text-gray-100 text-[11px] font-bold leading-tight">
                                                            {log.user}
                                                        </span>
                                                    </div>
                                                </td>
                                            )}

                                            {visibleColumns.action && (
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    {renderActionBadge(log.action)}
                                                </td>
                                            )}
                                            {visibleColumns.description && (
                                                <td className="px-3 py-2">
                                                    <div
                                                        className="text-gray-800 dark:text-gray-100 text-[10px] leading-relaxed max-w-2xl font-mono"
                                                        dangerouslySetInnerHTML={{ __html: parseDescription(log) }}
                                                    />
                                                </td>
                                            )}
                                            {visibleColumns.status && (
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${log.status.toLowerCase() === 'success'
                                                        ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-500/15 dark:text-green-400 dark:border-green-500/30'
                                                        : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/30'
                                                        }`}>
                                                        <div className={`w-1.5 h-1.5 rounded-full ${log.status.toLowerCase() === 'success' ? 'bg-green-500' : 'bg-red-500'
                                                            }`} />
                                                        {log.status}
                                                    </div>
                                                </td>
                                            )}
                                            {visibleColumns.created && (
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    <span className="text-gray-700 dark:text-gray-100 text-[10px] font-mono flex items-center gap-1.5">
                                                        {log.created
                                                            ? new Date(log.created.slice(0, 23) + "Z").toLocaleString()
                                                            : "-"}
                                                    </span>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 transition-colors duration-200">
                        <div className="flex items-center gap-4">
                            <ItemsPerPageDropdown
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                            />
                            <span className="text-[10px] text-gray-700 dark:text-gray-100 font-bold uppercase tracking-widest">
                                <span className="text-gray-900 dark:text-gray-100">
                                    {totalItems === 0 ? 0 : startIndex + 1}
                                </span>
                                {" - "}
                                <span className="text-gray-900 dark:text-gray-100">
                                    {Math.min(endIndex, totalItems)}
                                </span>
                                {" OF "}
                                {totalItems}
                            </span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                className="p-1.5 text-gray-600 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <ChevronsLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-1.5 text-gray-600 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>

                            <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-md shadow-orange-900/20">
                                {currentPage}
                            </div>

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-1.5 text-gray-600 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-1.5 text-gray-600 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <ChevronsRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
